import {
  computed,
  createEnvironmentInjector,
  DestroyRef,
  effect,
  EnvironmentInjector,
  inject,
  Injectable,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { Form, FormAnswer } from '@pixacare/forms';
import { FormBridge } from '@pixacare/forms-angular';
import { ModuleResolverInputs } from '../../../models/module-resolver-inputs';
import { EntityFormTypeKey } from '../models/entity-form-type-key';
import { FormGridEntityKey } from '../models/form-grid-entity-key';
import { FormGridGroupMode } from '../models/form-grid-group-mode';
import { FormInstance } from '../models/form-instance';
import { FormInstanceStatus } from '../models/form-instance-status';

type FormInstanceRecord = FormInstance & {
  bridge: WritableSignal<FormBridge>;
  date: WritableSignal<Date | string | null>;
  mode: WritableSignal<FormGridGroupMode>;
  status: WritableSignal<FormInstanceStatus | undefined>;
  isLoadedEmpty: boolean;
  injector: EnvironmentInjector;
  onBecameDirty?: (entityKey: string) => void;
};

type CreateInstanceParams = {
  entityKey: string;
  type: string;
  form: Form;
  answer: FormAnswer | null;
  date: Date | string | null;
  mode?: FormGridGroupMode;
  moduleInputs?: Signal<ModuleResolverInputs>;
  onBecameDirty?: (entityKey: string) => void;
};

@Injectable()
export class FormGridInstanceStore {
  private readonly parentInjector = inject(EnvironmentInjector);

  readonly HEADER_COLUMN = 'labels';
  readonly entityColumnKey = (entityKey: string): string => `entity-${entityKey}`;

  private readonly _entities = signal<readonly FormGridEntityKey[]>([]);
  private readonly _instances = signal<ReadonlyMap<EntityFormTypeKey, FormInstanceRecord>>(new Map());
  private readonly moduleInputsByKey = new Map<EntityFormTypeKey, Signal<ModuleResolverInputs>>();
  private readonly emptyModuleInputs = signal<ModuleResolverInputs>({});

  readonly entities = this._entities.asReadonly();
  readonly instancesMap = this._instances.asReadonly();

  constructor() {
    inject(DestroyRef).onDestroy(() => this.disposeAll());
  }

  key(entityKey: string, formType: string): EntityFormTypeKey {
    return `${entityKey}|${formType}` as EntityFormTypeKey;
  }

  setEntities(entities: readonly FormGridEntityKey[]): void {
    this._entities.set([...entities]);
  }

  appendEntity(entity: FormGridEntityKey): void {
    this._entities.update((entities) => [...entities, entity]);
  }

  dropEntity(entityKey: string): void {
    this._entities.update((entities) => entities.filter((entity) => entity.entityKey !== entityKey));
  }

  removeInstancesFor(entityKey: string, formTypes: readonly string[]): void {
    const current = this._instances();
    let next: Map<EntityFormTypeKey, FormInstanceRecord> | null = null;
    for (const formType of formTypes) {
      const key = this.key(entityKey, formType);
      const instance = current.get(key);
      if (!instance) {
        continue;
      }
      instance.injector.destroy();
      next ??= new Map(current);
      next.delete(key);
      this.moduleInputsByKey.delete(key);
    }
    if (next) {
      this._instances.set(next);
    }
  }

  createInstance(params: CreateInstanceParams): void {
    const key = this.key(params.entityKey, params.type);
    const current = this._instances();
    if (current.has(key)) {
      return;
    }
    const mode = params.mode ?? 'readonly';
    const { injector, bridge } = this.createBridge(params.form, params.answer, mode, key);
    const bridgeSignal = signal(bridge);
    const formAnswer = computed(() => bridgeSignal().formAnswer());
    const isDirty = computed(() => bridgeSignal().isFormDirty());
    const record: FormInstanceRecord = {
      entityKey: params.entityKey,
      type: params.type,
      bridge: bridgeSignal,
      date: signal(params.date),
      mode: signal<FormGridGroupMode>(mode),
      formAnswer,
      isDirty,
      status: signal<FormInstanceStatus | undefined>(undefined),
      isLoadedEmpty: params.answer === null,
      injector,
      onBecameDirty: params.onBecameDirty,
    };
    this.wireDirtyNotification(record);
    const next = new Map(current);
    next.set(key, record);
    this._instances.set(next);
    if (params.moduleInputs) {
      this.moduleInputsByKey.set(key, params.moduleInputs);
    }
  }

  swapBridge(entityKey: string, formType: string, form: Form, mode: FormGridGroupMode): void {
    const instance = this._instances().get(this.key(entityKey, formType));
    if (!instance) {
      return;
    }
    instance.injector.destroy();
    const { injector, bridge } = this.createBridge(form, null, mode, this.key(entityKey, formType));
    instance.injector = injector;
    instance.bridge.set(bridge);
    instance.status.set(undefined);
    this.wireDirtyNotification(instance);
  }

  setMode(entityKey: string, formType: string, mode: FormGridGroupMode): void {
    this._instances().get(this.key(entityKey, formType))?.mode.set(mode);
  }

  markInstancePristine(entityKey: string, formType: string): void {
    const instance = this._instances().get(this.key(entityKey, formType));
    if (!instance) {
      return;
    }
    const bridge = instance.bridge();
    bridge.markPristine();
    bridge.formGroup()?.markAsPristine();
  }

  setDate(entityKey: string, formType: string, date: Date | string | null): void {
    this._instances().get(this.key(entityKey, formType))?.date.set(date);
  }

  setStatus(entityKey: string, formType: string, status: FormInstanceStatus): void {
    this._instances().get(this.key(entityKey, formType))?.status.set(status);
  }

  clearStatus(entityKey: string, formType: string): void {
    this._instances().get(this.key(entityKey, formType))?.status.set(undefined);
  }

  status(entityKey: string, formType: string): FormInstanceStatus | undefined {
    return this._instances().get(this.key(entityKey, formType))?.status();
  }

  instance(entityKey: string, formType: string): FormInstance | undefined {
    return this._instances().get(this.key(entityKey, formType));
  }

  bridge(entityKey: string, formType: string): FormBridge | undefined {
    return this._instances().get(this.key(entityKey, formType))?.bridge();
  }

  date(entityKey: string, formType: string): Date | string | null {
    return this._instances().get(this.key(entityKey, formType))?.date() ?? null;
  }

  mode(entityKey: string, formType: string): FormGridGroupMode {
    return this._instances().get(this.key(entityKey, formType))?.mode() ?? 'readonly';
  }

  isLoadedEmpty(entityKey: string, formType: string): boolean {
    return this._instances().get(this.key(entityKey, formType))?.isLoadedEmpty ?? true;
  }

  moduleInputs(entityKey: string, formType: string): Signal<ModuleResolverInputs> {
    return this.moduleInputsByKey.get(this.key(entityKey, formType)) ?? this.emptyModuleInputs;
  }

  hasInstance(entityKey: string, formType: string): boolean {
    return this._instances().has(this.key(entityKey, formType));
  }

  instances(): readonly FormInstance[] {
    return [...this._instances().values()];
  }

  isAnyDirty(): boolean {
    for (const instance of this._instances().values()) {
      if (instance.isDirty()) {
        return true;
      }
    }
    return false;
  }

  private createBridge(
    form: Form,
    answer: FormAnswer | null,
    mode: FormGridGroupMode,
    key: EntityFormTypeKey,
  ): { injector: EnvironmentInjector; bridge: FormBridge } {
    const injector = createEnvironmentInjector([FormBridge], this.parentInjector, `FormBridge[${key}]`);
    const bridge = injector.get(FormBridge);
    bridge.initialize(form, answer ?? { answers: {} }, { ignoreDefaultAnswers: mode === 'readonly' });
    return { injector, bridge };
  }

  // Fires `onBecameDirty` once per false→true transition of the bridge's sticky
  // isFormDirty. The effect re-runs only when the signal value changes, and
  // sticky semantics guarantee no true→true re-emit — so a simple truthy check
  // suffices. markPristine flips it back to false, re-arming the next edit.
  private wireDirtyNotification(record: FormInstanceRecord): void {
    if (!record.onBecameDirty) {
      return;
    }
    effect(
      () => {
        if (record.bridge().isFormDirty()) {
          record.onBecameDirty?.(record.entityKey);
        }
      },
      { injector: record.injector },
    );
  }

  private disposeAll(): void {
    for (const record of this._instances().values()) {
      record.injector.destroy();
    }
    this._instances.set(new Map());
    this.moduleInputsByKey.clear();
  }
}
